import { State, useHookstate } from "@hookstate/core";
import { useUniversalFind, useUniversalFindAll } from "../../hooks/universal";
import { onInputChangeNative, showSnackbar } from "../../misc/utils";
import {
  DemeritType,
  DisciplinaryDefaultCaseTableState,
  MainUser,
  MainUserWithDemeritsAndSocialTranscriptItems,
} from "../../types";
import InputField from "../input-field/InputField";
import { DisciplinaryTableStudentsState } from "./DisciplinaryStudents";
import { FileUploadWithPreview } from "../FileUploadWithPreview";
import { ChangeEvent, useEffect, useState } from "react";
import DefaultButton from "../default-button/DefaultButton";
import ArrowLeft from "../../assets/icons/ArrowLeft";
import Tick from "../../assets/icons/Tick";
import { simpleGetter } from "../../actions/common";
import { demeritsApi } from "../../api/api";
import { Download } from "../../assets/icons/Download";
import CheckMark from "../../assets/icons/CheckMark";

export const DisciplinaryDemerit = ({
  parentState,
  selectedStudent,
  multiple,
  multipleSelectedStudents,
  mode = "edit",
  onClose,
}: {
  multiple?: boolean;
  parentState:
    | State<DisciplinaryTableStudentsState>
    | State<DisciplinaryDefaultCaseTableState>;
  selectedStudent:
    | MainUserWithDemeritsAndSocialTranscriptItems
    | MainUser
    | null;
  multipleSelectedStudents?: MainUserWithDemeritsAndSocialTranscriptItems[];
  mode?: string;
  onClose?: () => void;
}) => {
  const localState = useHookstate<DisciplinaryTableStudentsState>({
    files: {},
  });
  const selectedDemerit = parentState.selectedDemerit.get();
  const state = localState.get();
  const loading = Boolean(state.loading);
  const [shareDemerit, setShareDemerit] = useState(true);
  const demeritTypeSelected = state.demeritTypeSelected || "";
  const demeritDescription = state.demeritDescription || "";
  const demeritIncidentLink = state.demeritIncidentLink || "";
  const allDocumentLabels: Record<string, any>[] = useUniversalFind("Config", {
    key: "demerit_document_labels",
  });
  const documentLabels: any[] = allDocumentLabels[0]?.value || [];
  const demeritTypes: DemeritType[] = useUniversalFindAll("DemeritType");
  const isEdit = mode === "edit";

  useEffect(() => {
    if (selectedDemerit?.id) {
      localState.merge({
        demeritTypeSelected: String(selectedDemerit.demerit_type_id),
        demeritDescription: selectedDemerit.description,
        demeritIncidentLink: selectedDemerit.incident_link || "",
        files: Object.assign(
          {},
          ...selectedDemerit.media.map((m) => ({
            [m.demerit_media.label]: m,
          })),
        ),
      });
    }
  }, [selectedDemerit?.id]);

  const onDemeritFileChange =
    (l: string) => (f: ChangeEvent<HTMLInputElement>) => {
      if (f.target.files?.length) {
        localState.files[l].set(f.target.files[0]);
      }
    };

  const onDemeritFileUpdateRaw = (l: string) => (value: File | null) => {
    localState.files[l].set(value);
  };

  const validateForm = () => {
    if (!demeritTypeSelected || !demeritDescription) return false;

    for (const l of documentLabels) {
      if (!state.files[l.label]) {
        return false;
      }
    }

    return true;
  };

  const downloadTemplate = (documentLabelObject: any) => () => {
    const url = documentLabelObject.template;
    const downloadLink = document.createElement("a");
    downloadLink.href = url;
    downloadLink.download = documentLabelObject.label;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
    URL.revokeObjectURL(url);
  };

  const submit = async () => {
    const formData = new FormData();
    if (multiple) {
      formData.append("share_demerit", shareDemerit ? "1" : "0");
      formData.append("multiple", "1");
      const userIds = multipleSelectedStudents?.map((s) => s.id);
      formData.append("user_ids", JSON.stringify(userIds));
    } else {
      formData.append("multiple", "0");
      formData.append("user_ids", JSON.stringify([selectedStudent?.id]));
    }
    if (selectedDemerit?.id) {
      formData.append("id", String(selectedDemerit?.id));
    }
    formData.append("main_user_id", "" + selectedStudent?.id);
    formData.append("demerit_type_id", demeritTypeSelected);
    formData.append("description", demeritDescription);
    formData.append("incident_link", demeritIncidentLink);
    formData.append("files_stringified", JSON.stringify(state.files));
    for (const fileLabel in state.files) {
      if (state.files[fileLabel]) {
        formData.append(fileLabel, state.files[fileLabel] as File);
      }
    }
    localState.loading.set(true);
    const response = await simpleGetter(demeritsApi.createDemerit, formData);
    localState.loading.set(false);
    if (
      response?.success === false &&
      (response?.msg === "files_required" ||
        response?.message === "files_required")
    ) {
      showSnackbar(
        "Пожалуйста, загрузите обязательные файлы к нарушению.",
        "error",
      );
      return;
    }
    showSnackbar(
      response?.success ? "Успешно" : "Произошла ошибка",
      response?.success ? "success" : "error",
    );
    if (response?.success) {
      parentState.isAddDemeritModalOpen.set(false);
      parentState.studentsWithDemeritsTriggerFetch.set(
        (parentState.studentsWithDemeritsTriggerFetch.get() || 0) + 1,
      );
    }
  };
  return (
    <div className='flex flex-col flex-1 gap-8 overflow-y-auto'>
      <div className='flex flex-row gap-3'>
        <div className='flex-1 flex flex-col'>
          <span className='text-2xl text-valley-black mb-8'>
            {isEdit ? "Добавление нарушения" : "Просмотр нарушения"}
          </span>
          <InputField
            type='select'
            id='select-demerit-type'
            label='Тип нарушения'
            value={demeritTypeSelected}
            handleChange={onInputChangeNative(
              localState,
              "demeritTypeSelected",
            )}
            options={demeritTypes.map((d) => ({
              value: d.id,
              label: d.name,
            }))}
            disabled={!isEdit}
          />
        </div>
        <div className='flex-1 flex flex-col'>
          <InputField
            type='textarea'
            id='demerit-description'
            label='Описание'
            value={demeritDescription}
            handleChange={onInputChangeNative(localState, "demeritDescription")}
            additionalClassName='bg-valley-background-blue rounded-lg'
            disabled={!isEdit}
          />
        </div>
      </div>
      <InputField
        type='text'
        id='demerit-incident-link'
        label='Доказательства нарушений (фото/видео/скрин/письменное обращение пострадавших)'
        value={demeritIncidentLink}
        handleChange={onInputChangeNative(localState, "demeritIncidentLink")}
        additionalClassName='bg-valley-background-blue rounded-lg'
        disabled={!isEdit}
      />

      <div className='flex flex-col flex-1 overflow-y-auto gap-2'>
        {multiple && (
          <div className='flex flex-row items-center mt-4'>
            <span className='text-sm font-medium text-valley-gray'>
              Объединить выбранных студентов в одно дело
            </span>
            <input
              type='checkbox'
              className='ml-2 w-4 h-4'
              checked={shareDemerit}
              onChange={() => setShareDemerit(!shareDemerit)}
            />
          </div>
        )}
        {documentLabels.map((l) => (
          <div
            key={l.label}
            className='flex flex-row items-center py-[9px] gap-4'
          >
            <div className='flex-[2] flex flex-col gap-2'>
              <span className='text-sm font-medium text-valley-gray'>
                {l.label}
              </span>
              {Boolean(l.template) && isEdit && (
                <div className='flex-1 flex flex-row items-center p-3 rounded-lg gap-3 border bg-valley-background-blue border-valley-background-blue'>
                  <div className='flex flex-col flex-1'>
                    <span className='text-sm font-medium leading-[15.4px] text-valley-black truncate'>
                      Шаблон
                    </span>
                  </div>
                  <div className='flex flex-row gap-2'>
                    <button
                      onClick={downloadTemplate(l)}
                      className='bg-valley-background-blue p-[6px] rounded-md flex flex-row justify-center items-center'
                    >
                      <Download additionalStyle='w-6 h-6' />
                    </button>
                  </div>
                </div>
              )}
            </div>
            <div className='flex-[3] flex flex-row'>
              <FileUploadWithPreview
                mode={mode}
                buttonKeys={
                  isEdit ? ["view", "download", "delete"] : ["view", "download"]
                }
                accept={"application/pdf"}
                updateValueRaw={onDemeritFileUpdateRaw(l.label)}
                value={state.files[l.label]}
                handleChange={onDemeritFileChange(l.label)}
              />
            </div>
          </div>
        ))}
      </div>
      <div className='flex flex-row gap-3'>
        <DefaultButton
          Icon={ArrowLeft}
          buttonText='Вернуться'
          additionalStyle='flex-1 bg-valley-gray text-white py-3'
          {...(onClose
            ? { onClick: onClose }
            : {
                onClick: () => {
                  parentState.isAddDemeritModalOpen.set(false);
                },
              })}
        />
        {isEdit && (
          <DefaultButton
            Icon={Tick}
            buttonText='Сохранить'
            additionalStyle={`flex-1 text-white py-3 ${
              loading ? "bg-[#0068ff33]" : "bg-valley-main"
            }`}
            disabled={!validateForm()}
            onClick={submit}
            loading={loading}
          />
        )}
      </div>
    </div>
  );
};
